import React from 'react';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';

export interface RadioTabOption<T extends string = string> {
  label: string;
  value: T;
}

export interface RadioTabGroupProps<T extends string = string> {
  /** Currently selected value */
  value: T;
  /** Callback when selection changes */
  onChange: (value: T) => void;
  /** Array of tab options */
  options: RadioTabOption<T>[];
  /** Color when radio is selected (default: #E57373) */
  activeColor?: string;
  /** Color when radio is not selected (default: #B0B0B0) */
  inactiveColor?: string;
  /** Label text color when selected (default: #232323) */
  activeLabelColor?: string;
  /** Label text color when not selected (default: #888888) */
  inactiveLabelColor?: string;
  /** Font size for labels (default: 14) */
  labelFontSize?: number;
  /** Font weight for labels (default: 500) */
  labelFontWeight?: number;
  /** Radio icon size (default: 22) */
  radioSize?: number;
  /** Gap between radio options (default: 2) */
  gap?: number;
  /** Orientation of radio group */
  row?: boolean;
  /** Name attribute for the radio group */
  name?: string;
  /** Additional styles for the RadioGroup container */
  sx?: object;
}

/**
 * RadioTabGroup - A reusable radio button group component with customizable styling.
 * 
 * @example
 * ```tsx
 * const [status, setStatus] = useState<'active' | 'inactive'>('active');
 * 
 * <RadioTabGroup
 *   value={status}
 *   onChange={setStatus}
 *   options={[
 *     { label: 'Active', value: 'active' },
 *     { label: 'Inactive', value: 'inactive' },
 *   ]}
 * />
 * ```
 */
function RadioTabGroup<T extends string = string>({
  value,
  onChange,
  options,
  activeColor = '#E57373',
  inactiveColor = '#B0B0B0',
  activeLabelColor = '#232323',
  inactiveLabelColor = '#888888',
  labelFontSize = 14,
  labelFontWeight = 500,
  radioSize = 22,
  gap = 2,
  row = true,
  name,
  sx,
}: RadioTabGroupProps<T>): React.ReactElement {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value as T);
  };

  return (
    <RadioGroup
      row={row}
      value={value}
      onChange={handleChange}
      name={name}
      sx={{ gap, ...sx }}
    >
      {options.map((option) => {
        const isActive = value === option.value;

        return (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={
              <Radio
                sx={{
                  color: inactiveColor,
                  '&.Mui-checked': {
                    color: activeColor,
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: radioSize,
                  },
                }}
              />
            }
            label={option.label}
            sx={{
              '& .MuiFormControlLabel-label': {
                fontSize: labelFontSize,
                fontWeight: labelFontWeight,
                color: isActive ? activeLabelColor : inactiveLabelColor,
                transition: 'color 0.2s ease-in-out',
              },
            }}
          />
        );
      })}
    </RadioGroup>
  );
}

export default RadioTabGroup;
